import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useWallet } from "../wallet/WalletContext";
import { normalizeAssetUrl } from "../lib/assetUrl";
import { Button, Card, Field, Pill, inputClass } from "../components/ui";
import { FeeDisplay } from "../components/FeeDisplay";
import { WalletButton } from "../components/WalletButton";
import { useLauncher } from "../services/launcher/useLauncher";
import { useCreateEstimate } from "../services/launcher/useCreateEstimate";
import { useFactoryReadiness } from "../services/launcher/useFactoryReadiness";
import { parseSupply, validateName, validateSymbol, validateUrl } from "../lib/validation";
import type { RecipientEntry } from "../services/launcher/types";
import {
  validateDescription,
  validateLogoFile,
  collectMetadata,
  MAX_DESCRIPTION_CHARS,
} from "../lib/metadata";
import { byteLength, MAX_NAME_BYTES, MAX_SYMBOL_BYTES } from "../config/limits";
import { buildFeeDisplay, type FeeDisplayModel } from "../lib/feeDisplay";
import { formatEth, formatWholeTokens, truncateAddress, truncateHex } from "../lib/format";
import { explorerAddress, explorerTx } from "../lib/explorer";
import { receiptToBlob } from "../lib/receipt";
import { messageFor } from "../lib/errors";
import { ACTIVE_CHAIN } from "../config/chains";
import {
  LauncherError,
  type DeployPhase,
  type Hex,
  type LaunchOutcome,
} from "../services/launcher/types";

type Stage = "connect" | "configure" | "review" | "deploy" | "result";

/**
 * Returns true only when `url` is a relative path served by the SYNAPZ API
 * (e.g. `/objects/studio/uuid.png`).
 *
 * Rejects empty strings, absolute URLs (http / https), and any value that
 * contains known Replicate delivery CDN patterns — those must never reach
 * the Create review or post-launch card.
 */
export function isPermittedServingPath(url: unknown): url is string {
  if (typeof url !== "string" || url.length === 0) return false;
  // Must be a relative path — absolute URLs indicate an external CDN.
  if (!url.startsWith("/")) return false;
  // Belt-and-suspenders: block Replicate delivery CDN patterns explicitly.
  if (url.includes("replicate.com")) return false;
  if (url.includes("delivery.")) return false;
  return true;
}
const STAGES: Stage[] = ["connect", "configure", "review", "deploy", "result"];

const PHASE_LABEL: Record<DeployPhase, string> = {
  preparing: "Preparing",
  revalidatingFactory: "Revalidating factory",
  estimatingGas: "Estimating gas",
  checkingBalance: "Checking balance",
  awaitingApproval: "Awaiting wallet approval",
  submitted: "Transaction submitted",
  waitingReceipt: "Waiting for receipt",
  replaced: "Transaction replaced",
  verifying: "Verifying on-chain",
  confirmed: "Confirmed",
  reverted: "Reverted",
};


const ZERO = "0x0000000000000000000000000000000000000000";

/**
 * Validates an Ethereum checksummed or lowercased address (0x + 40 hex chars).
 * The zero address is NOT valid: the factory reverts `Market: zero recipient`,
 * and the unset placeholder must fail this check so the connected wallet
 * auto-fills the first recipient slot.
 */
function isValidAddress(addr: string): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(addr) && addr.toLowerCase() !== ZERO.toLowerCase();
}

/** Maximum creator reward recipients the ETH-v1 factory accepts. */
const MAX_RECIPIENTS = 5;
/** Total bps all recipients must sum to. */
const TOTAL_BPS = 10_000;

export default function Create() {
  const { address, isConnected, chainId, watchAsset } = useWallet();
  const { client, isDemo, isE2E, configError } = useLauncher();
  const liveMode = !isDemo && !isE2E && !configError;
  const factory = useFactoryReadiness(client, liveMode);

  const [stage, setStage] = useState<Stage>("connect");
  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [supply, setSupply] = useState("");
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");
  const [x, setX] = useState("");
  const [telegram, setTelegram] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | undefined>();
  // Single one-click acknowledgment: user confirms they have read the key disclosures.
  // Stored in sessionStorage so it survives multi-step navigation within the session
  // but resets automatically when the browser session ends.
  const [ackAccepted, setAckAccepted] = useState<boolean>(() => {
    try { return sessionStorage.getItem("synapz_create_ack") === "true"; } catch { return false; }
  });

  /**
   * Creator reward recipients for the ETH-v1 fair-launch createMarket call.
   * Default: single recipient = connected wallet at 10 000 bps (100%).
   * Initialised with ZERO address; auto-filled once the wallet connects.
   * The launcher pads to 5 slots when building the on-chain RecipientConfig.
   */
  const [recipients, setRecipients] = useState<RecipientEntry[]>([
    { address: ZERO as Hex, bps: TOTAL_BPS },
  ]);

  /** Permanent-storage URL for the AI Studio–approved token art (token_art asset). */
  const [studioTokenArtUrl, setStudioTokenArtUrl] = useState<string | null>(null);
  /** Permanent-storage URL for the AI Studio–approved launch banner. */
  const [studioBannerUrl, setStudioBannerUrl]     = useState<string | null>(null);

  /**
   * Session ID from the AI Launch Studio that transferred this draft.
   * Present only when the user arrives via the Studio → Create flow.
   * Used to associate the confirmed on-chain launch with the studio project.
   */
  const [studioSessionId, setStudioSessionId] = useState<string | null>(null);

  /**
   * Studio project association state.
   *   null     — no attempt yet (or no studio session)
   *   "linking" — in flight
   *   "done"   — success
   *   string  — error message (retry available)
   */
  const [associationState, setAssociationState] = useState<null | "linking" | "done" | string>(null);

  // ── Auto-fill the first recipient slot when the wallet connects ──────────
  useEffect(() => {
    if (!address) return;
    setRecipients((prev) => {
      const first = prev[0];
      // Fill the first slot only when it still holds the unset ZERO placeholder.
      if (prev.length === 1 && first && !isValidAddress(first.address)) {
        return [{ address: address as Hex, bps: first.bps ?? TOTAL_BPS }];
      }
      return prev;
    });
  }, [address]);

  // ── Recipient helpers ────────────────────────────────────────────────────
  function addRecipient() {
    setRecipients((prev) => prev.length < MAX_RECIPIENTS
      ? [...prev, { address: "" as Hex, bps: 0 }]
      : prev);
  }
  function removeRecipient(i: number) {
    setRecipients((prev) => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);
  }
  function updateRecipient(i: number, field: "address" | "bps", value: string | number) {
    setRecipients((prev) => prev.map((r, idx) =>
      idx === i
        ? { ...r, [field]: field === "address" ? String(value) as Hex : Math.max(0, Math.min(TOTAL_BPS, Number(value) || 0)) }
        : r
    ));
  }

  const totalBps = recipients.reduce((s, r) => s + r.bps, 0);
  const hasDuplicateAddress = (i: number) => {
    const entry = recipients[i];
    if (!entry) return false;
    const addr = entry.address.toLowerCase();
    return !!addr && addr !== ZERO.toLowerCase() &&
      recipients.some((r, j) => j !== i && r.address.toLowerCase() === addr);
  };
  const recipientsValid =
    recipients.length >= 1 &&
    recipients.length <= MAX_RECIPIENTS &&
    recipients.every((r) => isValidAddress(r.address)) &&
    !recipients.some((_, i) => hasDuplicateAddress(i)) &&
    totalBps === TOTAL_BPS;

  const [phase, setPhase] = useState<DeployPhase | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [outcome, setOutcome] = useState<LaunchOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [feeChanged, setFeeChanged] = useState(false);

  const nameRes = validateName(name);
  const symbolRes = validateSymbol(symbol);
  const supplyRes = parseSupply(supply);
  const descRes = validateDescription(description);
  const linksValid = [website, x, telegram].every((u) => validateUrl(u).ok);
  const configValid = nameRes.ok && symbolRes.ok && supplyRes.ok && descRes.ok && linksValid && recipientsValid;

  const previewAccount = (address ?? "0x0000000000000000000000000000000000000001") as Hex;

  // Persist single acknowledgment to sessionStorage (survives step navigation, resets on new session).
  useEffect(() => {
    try { sessionStorage.setItem("synapz_create_ack", String(ackAccepted)); } catch { /* ignore */ }
  }, [ackAccepted]);

  const prevLogo = useRef<string | null>(null);
  useEffect(() => {
    prevLogo.current = logoUrl;
    return () => {
      if (prevLogo.current) URL.revokeObjectURL(prevLogo.current);
    };
  }, [logoUrl]);

  // Consume pre-fill data written by AI Launch Studio (Transfer to Create).
  // Validates structure/size, applies only to empty fields, then clears the key
  // so a stale draft cannot silently re-apply on a later visit.
  useEffect(() => {
    const PREFILL_KEY = "synapz_create_prefill";
    // Raised from 4096 → 16384 to accommodate approvedAssets serving paths.
    const MAX_BYTES = 16384;
    try {
      const raw = localStorage.getItem(PREFILL_KEY);
      if (!raw) return;
      // Reject oversized payloads
      if (raw.length > MAX_BYTES) {
        localStorage.removeItem(PREFILL_KEY);
        return;
      }
      const parsed: unknown = JSON.parse(raw);
      // Structural validation — must be a plain object
      if (
        typeof parsed !== "object" ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        localStorage.removeItem(PREFILL_KEY);
        return;
      }
      const p = parsed as Record<string, unknown>;
      const safeName      = typeof p.name       === "string" ? p.name.slice(0, 64)               : "";
      const safeTicker    = typeof p.ticker     === "string" ? p.ticker.slice(0, 12).toUpperCase() : "";
      const safeDesc      = typeof p.description=== "string" ? p.description.slice(0, 300)        : "";
      const safeWebsite   = typeof p.website    === "string" ? p.website.slice(0, 200)            : "";
      const safeX         = typeof p.x          === "string" ? p.x.slice(0, 200)                  : "";
      const safeTelegram  = typeof p.telegram   === "string" ? p.telegram.slice(0, 200)            : "";
      // Studio session ID — validated as UUID format
      const safeSessionId = typeof p.sessionId  === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(p.sessionId)
        ? p.sessionId : null;
      // Apply only to fields that are currently empty (never overwrite user input)
      if (safeName)      setName((prev)          => prev || safeName);
      if (safeTicker)    setSymbol((prev)        => prev || safeTicker);
      if (safeDesc)      setDescription((prev)   => prev || safeDesc);
      if (safeWebsite)   setWebsite((prev)       => prev || safeWebsite);
      if (safeX)         setX((prev)             => prev || safeX);
      if (safeTelegram)  setTelegram((prev)       => prev || safeTelegram);
      if (safeSessionId) setStudioSessionId(safeSessionId);

      // Apply approved studio assets (relative SYNAPZ serving paths only).
      // Guard: isPermittedServingPath rejects absolute URLs and any Replicate
      // CDN pattern — those must never reach the Create review or result card.
      if (Array.isArray(p.approvedAssets)) {
        for (const a of p.approvedAssets as unknown[]) {
          if (typeof a !== "object" || a === null) continue;
          const asset = a as Record<string, unknown>;
          if (!isPermittedServingPath(asset.servingPath)) continue;
          // Normalize legacy `/objects/...` paths (stored by older drafts)
          // to the routable `/api/storage/objects/...` form.
          const path = normalizeAssetUrl(asset.servingPath as string);
          if (asset.assetType === "token_art") setStudioTokenArtUrl(path);
          if (asset.assetType === "banner")    setStudioBannerUrl(path);
        }
      }

      // Mark consumed — remove so it does not re-apply on refresh
      localStorage.removeItem(PREFILL_KEY);
    } catch {
      // Malformed JSON — silently clear
      try { localStorage.removeItem(PREFILL_KEY); } catch { /* ignore */ }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-quote fee + re-estimate gas on any transaction-affecting change, clearing
  // stale state and discarding stale async responses (see useCreateEstimate).
  const supplyOk = supplyRes.ok;
  const supplyWhole = supplyRes.whole;
  const { quote, gas, quoteLoading, gasLoading, gasError } = useCreateEstimate(
    client,
    previewAccount,
    name,
    symbol,
    supplyOk,
    supplyWhole,
    refreshNonce,
    // ETH-v1 canonical factory only implements createMarket — gas estimation
    // must use the same path (and same recipients) as the actual deploy.
    recipients
  );

  const gasAvailable = gas !== null;
  const quoteAvailable = quote !== null;

  const feeModel: FeeDisplayModel | null = useMemo(
    () => (quote ? buildFeeDisplay(quote, gas, { demo: isDemo }) : null),
    [quote, gas, isDemo]
  );

  function onLogo(file: File | undefined) {
    if (logoUrl) URL.revokeObjectURL(logoUrl);
    setLogoUrl(null);
    setLogoError(undefined);
    if (!file) return;
    const res = validateLogoFile(file);
    if (!res.ok) {
      setLogoError(res.error);
      return;
    }
    setLogoUrl(res.objectUrl ?? null);
  }

  /**
   * Fire-and-forget: links a confirmed on-chain launch to its AI Launch Studio
   * session.  Failure here is non-fatal — the blockchain launch is always the
   * canonical source of truth.  Any error is surfaced to the user with a retry
   * button; the association can be retried indefinitely (idempotent endpoint).
   */
  async function associateLaunch(result: LaunchOutcome, sessionId: string) {
    const r = result.receipt;
    const tokenAddress = r.tokenAddress;
    const txHash       = r.txHash;
    if (!tokenAddress || !txHash || !address) return;

    setAssociationState("linking");
    try {
      // Step 1 — request a one-time nonce
      const nonceRes = await fetch("/api/auth/nonce", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          wallet: address.toLowerCase(),
          action: "associate_launch",
          chainId: r.chainId,
          contractAddress: tokenAddress.toLowerCase(),
        }),
      });
      if (!nonceRes.ok) {
        const err = (await nonceRes.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? `Nonce request failed (${nonceRes.status})`);
      }
      const { nonce, message } = (await nonceRes.json()) as { nonce: string; message: string };

      // Step 2 — sign the nonce with the connected wallet
      const signature = await (window.ethereum as {
        request(args: { method: string; params: unknown[] }): Promise<string>;
      }).request({
        method: "personal_sign",
        params: [message, address],
      });

      // Step 3 — POST the association
      const assocRes = await fetch(`/api/ai/studio-projects/${sessionId}/associate-launch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nonce,
          signature,
          txHash: txHash.toLowerCase(),
          chainId: r.chainId,
          tokenAddress: tokenAddress.toLowerCase(),
          recipients: recipients
            .filter((rec) => isValidAddress(rec.address) && rec.bps > 0)
            .map((rec) => ({ address: rec.address.toLowerCase(), bps: rec.bps })),
        }),
      });

      if (!assocRes.ok && assocRes.status !== 200 && assocRes.status !== 201) {
        const err = (await assocRes.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? `Association failed (${assocRes.status})`);
      }

      setAssociationState("done");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Studio project linking failed";
      setAssociationState(msg);
      // Persist for retry — store partial data in localStorage
      try {
        localStorage.setItem(
          "synapz_assoc_retry",
          JSON.stringify({ sessionId, txHash, chainId: r.chainId, tokenAddress, ts: Date.now() }),
        );
      } catch { /* ignore */ }
    }
  }

  async function runDeploy() {
    if (isDemo || !address) return;
    setError(null);
    setFeeChanged(false);
    try {
      const result = await client.createToken(
        { name, symbol, supplyWhole: supplyRes.whole!, recipients },
        address as Hex,
        { knownFeeAtReview: quote?.effectiveFee, onPhase: setPhase }
      );
      setOutcome(result);
      setStage("result");
      // Fire-and-forget studio project association (non-blocking, failure is safe).
      if (studioSessionId && result.receipt.txHash && result.receipt.tokenAddress !== ZERO) {
        void associateLaunch(result, studioSessionId);
      }
    } catch (e) {
      const kind = e instanceof LauncherError ? e.kind : "unknown";
      if (kind === "feeChanged") {
        setFeeChanged(true);
        setRefreshNonce((n) => n + 1); // re-quote the new on-chain fee
        setStage("review");
      }
      setError(messageFor(kind));
    } finally {
      setPhase(null);
    }
  }

  function downloadReceipt() {
    if (!outcome) return;
    const meta = collectMetadata({ description, website, x, telegram, hasLogo: !!logoUrl });
    const url = URL.createObjectURL(receiptToBlob(outcome, meta));
    const a = document.createElement("a");
    a.href = url;
    a.download = `synapz-launch-${outcome.receipt.symbol || "token"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <header className="space-y-2">
        <h1 className="font-display text-3xl font-bold">Create a token</h1>
        <Stepper stage={stage} />
      </header>

      {stage === "connect" && (
        <Card className="space-y-4">
          <h2 className="font-display text-lg">1 · Connect your wallet</h2>
          <p className="text-sm text-muted">
            Connect an injected wallet (MetaMask, Rabby) on {ACTIVE_CHAIN.label}. You sign every
            action yourself — we never ask for a private key or seed phrase.
          </p>
          <div className="flex items-center gap-3">
            <WalletButton />
            {ACTIVE_CHAIN.faucetUrl && (
              <a className="text-sm text-plasma hover:underline" href={ACTIVE_CHAIN.faucetUrl} target="_blank" rel="noreferrer">
                Get testnet ETH ↗
              </a>
            )}
          </div>
          <div className="flex justify-end">
            <Button disabled={!isConnected} onClick={() => setStage("configure")}>Continue</Button>
          </div>
        </Card>
      )}

      {stage === "configure" && (
        <Card className="space-y-4">
          <h2 className="font-display text-lg">2 · Configure</h2>
          <Field label="Token name" error={name ? nameRes.error : undefined} hint={`${byteLength(name)}/${MAX_NAME_BYTES} bytes`}>
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Synapz Demo Token" />
          </Field>
          <Field label="Symbol" error={symbol ? symbolRes.error : undefined} hint={`${byteLength(symbol)}/${MAX_SYMBOL_BYTES} bytes`}>
            <input className={inputClass} value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="SDEMO" />
          </Field>
          <Field label="Total supply (whole tokens)" error={supply ? supplyRes.error : undefined} hint="Whole numbers only — no decimals, no commas.">
            <input className={inputClass} inputMode="numeric" value={supply} onChange={(e) => setSupply(e.target.value)} placeholder="1000000" />
          </Field>

          {/* ── Creator reward recipients ─────────────────────────────────── */}
          <div className="border-t border-line pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted">Creator reward recipients</p>
              <span className={`text-xs font-mono ${totalBps === TOTAL_BPS ? "text-positive" : "text-danger"}`}>
                {totalBps.toLocaleString()} / {TOTAL_BPS.toLocaleString()} bps
              </span>
            </div>
            <p className="text-xs text-muted leading-relaxed">
              Trading fees and post-graduation Uniswap V3 LP earnings are split between these
              addresses on-chain. You may add up to 5 recipients; percentages must total
              exactly 10,000 bps (100%). The last recipient absorbs any integer-division residue.
            </p>
            {recipients.map((r, i) => (
              <div key={i} className="flex gap-2 items-start">
                <div className="flex-1 space-y-1">
                  <input
                    className={inputClass}
                    placeholder="0x… wallet address"
                    value={r.address}
                    onChange={(e) => updateRecipient(i, "address", e.target.value)}
                  />
                  {r.address && !isValidAddress(r.address) && (
                    <p className="text-xs text-danger">Invalid address — must be 0x + 40 hex characters.</p>
                  )}
                  {hasDuplicateAddress(i) && (
                    <p className="text-xs text-danger">Duplicate address.</p>
                  )}
                </div>
                <div className="w-24 space-y-1">
                  <input
                    className={inputClass}
                    type="number"
                    min={1}
                    max={TOTAL_BPS}
                    step={1}
                    placeholder="bps"
                    value={r.bps || ""}
                    onChange={(e) => updateRecipient(i, "bps", e.target.value)}
                  />
                  <p className="text-[10px] text-muted text-right">{r.bps > 0 ? (r.bps / 100).toFixed(2) + "%" : ""}</p>
                </div>
                {recipients.length > 1 && (
                  <button
                    type="button"
                    className="mt-2 text-xs text-danger hover:opacity-70 shrink-0"
                    onClick={() => removeRecipient(i)}
                    aria-label={`Remove recipient ${i + 1}`}
                  >✕</button>
                )}
              </div>
            ))}
            <div className="flex items-center gap-3">
              {recipients.length < MAX_RECIPIENTS && (
                <Button variant="ghost" onClick={addRecipient}>+ Add recipient</Button>
              )}
              {address && (
                <Button variant="ghost" onClick={() => updateRecipient(0, "address", address)}>
                  Use connected wallet
                </Button>
              )}
            </div>
            {totalBps !== TOTAL_BPS && (
              <p className="text-xs text-danger">
                Total must be exactly {TOTAL_BPS.toLocaleString()} bps. Currently {totalBps.toLocaleString()} bps
                {totalBps < TOTAL_BPS ? ` (${(TOTAL_BPS - totalBps).toLocaleString()} bps remaining)` : ` (${(totalBps - TOTAL_BPS).toLocaleString()} bps over)`}.
              </p>
            )}
          </div>

          <div className="border-t border-line pt-4 space-y-4">
            <p className="text-xs text-muted">
              Optional metadata below is used only in this interface and your receipt. It is NOT
              stored in the ERC-20 contract and never uploaded.
            </p>
            {studioTokenArtUrl && (
              <div className="space-y-1">
                <p className="text-xs text-muted font-semibold">AI Studio token art (from Launch Studio)</p>
                <img
                  src={studioTokenArtUrl}
                  alt="Studio-approved token art"
                  className="h-20 w-20 rounded-xl border border-plasma/30 object-cover"
                />
              </div>
            )}
            {studioBannerUrl && (
              <div className="space-y-1">
                <p className="text-xs text-muted font-semibold">AI Studio launch banner (from Launch Studio)</p>
                <img
                  src={studioBannerUrl}
                  alt="Studio-approved launch banner"
                  className="w-full rounded-xl border border-plasma/30 object-cover"
                  style={{ maxHeight: "100px" }}
                />
              </div>
            )}
            <Field label="Logo (optional, local preview only)" error={logoError}>
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className={inputClass} onChange={(e) => onLogo(e.target.files?.[0])} />
            </Field>
            {logoUrl && <img src={logoUrl} alt="Logo preview" className="h-16 w-16 rounded-lg border border-line object-cover" />}
            <Field label="Description (optional)" error={description ? descRes.error : undefined} hint={`${description.length}/${MAX_DESCRIPTION_CHARS}`}>
              <textarea className={inputClass} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field label="Website (optional)" error={website ? validateUrl(website).error : undefined}>
              <input className={inputClass} value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" />
            </Field>
            <Field label="X profile (optional)" error={x ? validateUrl(x).error : undefined}>
              <input className={inputClass} value={x} onChange={(e) => setX(e.target.value)} placeholder="https://x.com/…" />
            </Field>
            <Field label="Telegram (optional)" error={telegram ? validateUrl(telegram).error : undefined}>
              <input className={inputClass} value={telegram} onChange={(e) => setTelegram(e.target.value)} placeholder="https://t.me/…" />
            </Field>
          </div>

          {liveMode && !factory.ready && (
            <p className="text-sm text-danger">
              {factory.readiness === "loading"
                ? "Verifying factory on-chain…"
                : `Token creation is unavailable — ${factory.reason}`}
            </p>
          )}
          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStage("connect")}>Back</Button>
            <Button disabled={!configValid || (liveMode && !factory.ready)} onClick={() => setStage("review")}>Review</Button>
          </div>
        </Card>
      )}

      {stage === "review" && (
        <div className="space-y-4">
          {feeChanged && (
            <Card className="border-warn/40">
              <p className="text-sm text-warn">The effective fee changed. Review the updated amount below and confirm again.</p>
            </Card>
          )}
          <Card className="space-y-3">
            <h2 className="font-display text-lg">3 · Review</h2>
            <dl className="text-sm divide-y divide-line">
              <SummaryRow label="Name" value={name} />
              <SummaryRow label="Symbol" value={symbol} />
              <SummaryRow label="Supply (whole)" value={supplyRes.ok ? formatWholeTokens(supplyRes.whole!) : "—"} />
              <SummaryRow label="Supply (smallest units)" value={supplyRes.ok ? supplyRes.smallestUnits!.toString() : "—"} />
              <SummaryRow label="Creator" value={address ? truncateAddress(address) : "—"} />
              <SummaryRow label="Network" value={`${ACTIVE_CHAIN.label} (${ACTIVE_CHAIN.chainId})`} />
              <SummaryRow label="Recipient" value="Full supply is minted to your wallet" />
            </dl>
          </Card>
          {quoteLoading && <p className="text-sm text-muted">Loading fee quote…</p>}
          {!quoteLoading && !quoteAvailable && (
            <p className="text-sm text-danger">Fee quote unavailable — reconnect or try again before deploying.</p>
          )}
          {feeModel && <FeeDisplay model={feeModel} />}
          {(studioTokenArtUrl || studioBannerUrl) && (
            <Card className="space-y-3">
              <h3 className="text-sm font-semibold">AI Studio assets</h3>
              <p className="text-xs text-muted">Approved in Launch Studio — served from permanent storage, never a temporary URL.</p>
              {studioTokenArtUrl && (
                <div className="space-y-1">
                  <p className="text-xs text-muted">Token art</p>
                  <img
                    src={studioTokenArtUrl}
                    alt="Approved token art"
                    className="h-24 w-24 rounded-xl border border-plasma/30 object-cover"
                  />
                </div>
              )}
              {studioBannerUrl && (
                <div className="space-y-1">
                  <p className="text-xs text-muted">Launch banner</p>
                  <img
                    src={studioBannerUrl}
                    alt="Approved launch banner"
                    className="w-full rounded-xl border border-plasma/30 object-cover"
                    style={{ maxHeight: "120px" }}
                  />
                </div>
              )}
            </Card>
          )}
          {(description || website || x || telegram || logoUrl) && (
            <Card className="space-y-2">
              <h3 className="text-sm font-semibold">Off-chain metadata</h3>
              <p className="text-xs text-muted">Used only in this interface and your receipt — not stored in the ERC-20 contract.</p>
              <dl className="text-sm divide-y divide-line">
                {logoUrl && <SummaryRow label="Logo" value="Provided (local preview only)" />}
                {description && <SummaryRow label="Description" value={description} />}
                {website && <SummaryRow label="Website" value={website} />}
                {x && <SummaryRow label="X" value={x} />}
                {telegram && <SummaryRow label="Telegram" value={telegram} />}
              </dl>
            </Card>
          )}
          <Card className="space-y-2">
            <h3 className="text-sm font-semibold">Creator reward recipients</h3>
            <p className="text-xs text-muted">Fixed on-chain at launch — cannot be changed after deployment.</p>
            <dl className="text-sm divide-y divide-line">
              {recipients.map((r, i) => (
                <SummaryRow
                  key={i}
                  label={`Recipient ${i + 1}`}
                  value={`${truncateAddress(r.address as Hex)} — ${r.bps.toLocaleString()} bps (${(r.bps / 100).toFixed(2)}%)`}
                />
              ))}
            </dl>
            <div className="pt-1 space-y-1 text-xs text-muted">
              <p>Curve creator fee: <span className="text-foreground">50 bps</span> of gross trade value (protocol: 50 bps).</p>
              <p>Post-graduation V3 LP fees: <span className="text-foreground">50% creator</span> / 50% protocol.</p>
              <p>Creation fee: <span className="text-foreground">0.0001 ETH</span> (non-refundable).</p>
            </div>
          </Card>
          <Card className="space-y-2 text-xs">
            <p className="font-semibold text-danger">⚠ UNAUDITED BETA — Smart contracts have not received an independent external audit. Bugs or exploits could result in permanent, irreversible loss of all funds committed. Do not commit funds you cannot afford to lose.</p>
            <p className="text-warn">Blockchain transactions are normally irreversible.</p>
            <p className="text-warn">Creator reward recipients and their split percentages are fixed on-chain at launch — they cannot be changed after deployment.</p>
            <p className="text-warn">Launching creates no liquidity and no trading market.</p>
            <p className="text-warn">Deploying on Robinhood Chain does not list the token in the Robinhood app.</p>
            <p className="text-warn">This tool is independent and not affiliated with or endorsed by Robinhood.</p>
          </Card>
          <Card className="space-y-3">
            <p className="text-xs font-semibold text-muted">Acknowledgment required</p>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="create-ack-checkbox">
              <input
                type="checkbox"
                className="mt-1 shrink-0 accent-plasma"
                checked={ackAccepted}
                onChange={(e) => setAckAccepted(e.target.checked)}
              />
              <span className="text-sm text-muted leading-relaxed">
                I have read the{" "}
                <Link to="/legal/smart-contract-risk" target="_blank" rel="noreferrer" className="underline text-plasma hover:opacity-80">
                  Risk Disclosure
                </Link>{" "}
                and{" "}
                <Link to="/legal/irreversible-transactions" target="_blank" rel="noreferrer" className="underline text-plasma hover:opacity-80">
                  Irreversible Transaction Warning
                </Link>
                . I understand this platform is in{" "}
                <Link to="/legal/unaudited-beta" target="_blank" rel="noreferrer" className="underline text-plasma hover:opacity-80">
                  unaudited beta
                </Link>
                , all blockchain transactions are irreversible, and I may lose all committed funds.
              </span>
            </label>
          </Card>
          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStage("configure")}>Back</Button>
            <Button disabled={!ackAccepted || (liveMode && !factory.ready)} onClick={() => setStage("deploy")}>Continue</Button>
          </div>
        </div>
      )}

      {stage === "deploy" && (
        <Card className="space-y-4">
          <h2 className="font-display text-lg">4 · Deploy</h2>
          {isDemo ? (
            <div className="space-y-3">
              <Pill tone="warn">Demonstration mode</Pill>
              <p className="text-sm text-muted">
                Testnet factory not deployed. Blockchain deployment is currently disabled.
                Everything above is real validation and fee logic; only the on-chain deploy step
                is switched off until a verified factory address is configured. No transaction is
                produced in demonstration mode.
              </p>
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStage("review")}>Back</Button>
                <Button disabled title="Deployment disabled in demonstration mode">Deploy (disabled)</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {chainId !== ACTIVE_CHAIN.chainId && (
                <Pill tone="danger">Wrong network — switch to {ACTIVE_CHAIN.label}</Pill>
              )}
              {liveMode && !factory.ready && (
                <p className="text-sm text-danger">
                  {factory.readiness === "loading" ? "Verifying factory on-chain…" : `Deployment blocked — ${factory.reason}`}
                </p>
              )}
              {!gasAvailable && (
                <p className="text-sm text-warn">
                  {gasLoading ? "Estimating gas…" : "Gas estimate unavailable — deployment is blocked until a fresh estimate succeeds."}
                </p>
              )}
              {!gasAvailable && !gasLoading && gasError && (
                <p className="text-sm text-danger" data-testid="gas-error-reason">{gasError}</p>
              )}
              {phase && <p className="text-sm text-plasma">{PHASE_LABEL[phase]}…</p>}
              {error && <p className="text-sm text-danger">{error}</p>}
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStage("review")} disabled={!!phase}>Back</Button>
                <Button
                  onClick={() => void runDeploy()}
                  disabled={!!phase || chainId !== ACTIVE_CHAIN.chainId || !gasAvailable || !quoteAvailable || (liveMode && !factory.ready)}
                >
                  {phase ? "Working…" : "Deploy"}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {stage === "result" && outcome && (
        <ResultView
          outcome={outcome}
          onWatch={watchAsset}
          onDownload={downloadReceipt}
          onRestart={() => {
            setOutcome(null);
            setStage("connect");
          }}
          associationState={associationState}
          onRetryAssociation={studioSessionId
            ? () => { setAssociationState(null); void associateLaunch(outcome, studioSessionId); }
            : undefined}
        />
      )}
    </div>
  );
}

function ResultView({
  outcome,
  onWatch,
  onDownload,
  onRestart,
  associationState,
  onRetryAssociation,
}: {
  outcome: LaunchOutcome;
  onWatch: (t: { address: Hex; symbol: string; decimals: number }) => Promise<boolean>;
  onDownload: () => void;
  onRestart: () => void;
  associationState?: null | "linking" | "done" | string;
  onRetryAssociation?: () => void;
}) {
  const r = outcome.receipt;
  const verified = outcome.verificationPassed;
  const canWatch = outcome.transactionConfirmed && r.tokenAddress !== ZERO;
  return (
    <div className="space-y-4">
      <Card className={`space-y-2 ${verified ? "border-plasma/40" : "border-danger/50"}`}>
        <h2 className="font-display text-lg">5 · Result</h2>
        {verified ? (
          <Pill tone="plasma">Launch verified on-chain</Pill>
        ) : (
          <div className="space-y-1">
            <Pill tone="danger">Transaction confirmed, but post-deployment verification failed</Pill>
            <p className="text-sm text-danger">Do not treat this as a verified launch.</p>
          </div>
        )}

        {/* Studio project association status — non-blocking, shown only when a session exists */}
        {associationState === "linking" && (
          <p className="text-xs text-muted">Linking to AI Launch Studio project…</p>
        )}
        {associationState === "done" && (
          <p className="text-xs text-plasma">✓ Linked to AI Launch Studio project</p>
        )}
        {associationState && associationState !== "linking" && associationState !== "done" && (
          <div className="space-y-1">
            <p className="text-xs text-warning">Studio project linking failed — your launch is still confirmed on-chain.</p>
            {onRetryAssociation && (
              <Button variant="ghost" onClick={onRetryAssociation}>
                Retry link
              </Button>
            )}
          </div>
        )}
      </Card>

      <Card>
        <dl className="text-sm divide-y divide-line">
          <SummaryRow label="Name" value={r.name} />
          <SummaryRow label="Symbol" value={r.symbol} />
          <SummaryRow label="Decimals" value={String(r.decimals)} />
          <SummaryRow label="Supply (whole)" value={formatWholeTokens(r.supplyWhole)} />
          <SummaryRow label="Supply (smallest units)" value={r.supplySmallestUnits.toString()} />
          <SummaryRow label="Token" value={truncateAddress(r.tokenAddress)} />
          <SummaryRow label="Creator" value={truncateAddress(r.creator)} />
          <SummaryRow label="Factory" value={r.factoryAddress ? truncateAddress(r.factoryAddress) : "—"} />
          <SummaryRow label="Transaction" value={truncateHex(r.txHash)} />
          <SummaryRow label="Block" value={r.blockNumber.toString()} />
          <SummaryRow label="Chain" value={String(r.chainId)} />
          <SummaryRow label="Effective fee" value={formatEth(r.feePaid)} />
        </dl>
      </Card>

      <Card className="space-y-2">
        <h3 className="text-sm font-semibold">Verification checks</h3>
        <ul className="text-xs space-y-1">
          {outcome.verification.checks.map((c) => (
            <li key={c.key} className="flex items-center gap-2">
              <span className={c.passed ? "text-plasma" : "text-danger"}>{c.passed ? "✓" : "✗"}</span>
              <span className="text-muted">{c.label}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="flex flex-wrap gap-2">
        <a href={explorerTx(r.txHash)} target="_blank" rel="noreferrer"><Button variant="ghost">Transaction ↗</Button></a>
        <a href={explorerAddress(r.tokenAddress)} target="_blank" rel="noreferrer"><Button variant="ghost">Token ↗</Button></a>
        <a href={`/token/${r.chainId}/${r.tokenAddress}`}><Button variant="ghost">Token Profile</Button></a>
        <a href={explorerAddress(r.creator)} target="_blank" rel="noreferrer"><Button variant="ghost">Creator ↗</Button></a>
        {r.factoryAddress && (
          <a href={explorerAddress(r.factoryAddress)} target="_blank" rel="noreferrer"><Button variant="ghost">Factory ↗</Button></a>
        )}
        <Button variant="ghost" onClick={() => void navigator.clipboard?.writeText(r.tokenAddress)}>Copy token address</Button>
        {canWatch && (
          <Button variant="ghost" onClick={() => void onWatch({ address: r.tokenAddress, symbol: r.symbol, decimals: r.decimals })}>
            Add token to wallet
          </Button>
        )}
        <Button onClick={onDownload}>Download receipt JSON</Button>
      </Card>

      <Button variant="ghost" onClick={onRestart}>Start over</Button>
    </div>
  );
}

function Stepper({ stage }: { stage: Stage }) {
  const idx = STAGES.indexOf(stage);
  return (
    <div>
      {/* Step pills */}
      <ol className="flex flex-wrap gap-2 text-xs mb-3">
        {STAGES.map((s, i) => {
          const done = i < idx;
          const active = i === idx;
          return (
            <li
              key={s}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 capitalize transition-colors font-medium ${
                active
                  ? "border-plasma/60 text-plasma bg-plasma/10"
                  : done
                    ? "border-line text-ink bg-line/30"
                    : "border-line/50 text-muted"
              }`}
            >
              {done ? (
                <svg aria-hidden="true" width="10" height="10" viewBox="0 0 10 10" fill="currentColor" className="text-plasma">
                  <path d="M8.5 2.5L4 7.5 1.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              ) : (
                <span
                  className={`inline-flex w-4 h-4 items-center justify-center rounded-full text-[10px] font-bold ${
                    active ? "bg-plasma text-void" : "bg-line/60 text-muted"
                  }`}
                >
                  {i + 1}
                </span>
              )}
              {s}
            </li>
          );
        })}
      </ol>
      {/* Progress bar */}
      <div className="h-[2px] bg-line rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-plasma to-plasma2 rounded-full transition-all duration-500"
          style={{ width: `${((idx) / (STAGES.length - 1)) * 100}%` }}
        />
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2 gap-4">
      <dt className="text-muted shrink-0">{label}</dt>
      <dd className="text-ink text-right font-mono break-all">{value}</dd>
    </div>
  );
}
